import classNames from 'classnames';
import capitalize from 'lodash/capitalize';
import React, { use } from 'react';

import { WebsiteLink } from 'src/components/common/website-link';
import ArrowRightThin from 'src/components/icons/arrow-right-thin';
import RedesignRightChevronThin from 'src/components/icons/redesign-right-chevron-thin';
import { levelThreeMenuTopStyling } from 'src/components/layout/redesign/header-desktop/heder-desktop-helper';
import { SiteContext } from 'src/context/site-context';
import { amplitudeLinkTrackEvent } from 'src/helpers/amplitude-event-track';
import { pathToTitle } from 'src/helpers/string-helper';

import * as styles from './header-level-two-menu.module.scss';

type LevelTwoMenuProps = {
  selected: boolean;
  levelTwoMenu: Queries.HeaderLevelTwoMenuItem;
  onSelectMenu: () => void;
  updateRef: (ref: HTMLUListElement | null) => void;
  onFinish?: () => void;
  menuPosition: number;
  totalLevelTwoItems: number;
};

const HeaderDesktopLevelTwoMenu = ({
  selected,
  levelTwoMenu,
  onSelectMenu,
  updateRef,
  onFinish,
  menuPosition,
  totalLevelTwoItems,
}: LevelTwoMenuProps) => {
  const { styling, pageLevel, isMeridian, buildType, isHarbour, language } = use(SiteContext);
  const levelThreeTop = levelThreeMenuTopStyling({
    levelTwoPosition: menuPosition,
    totalLevelTwoItems: totalLevelTwoItems,
    totalLevelThreeItems: levelTwoMenu.levelThreeMenuItems?.length ?? 0,
  });

  const hasLevelThreeItems = !!levelTwoMenu.levelThreeMenuItems?.length;

  const handleClickSecondLevel = () => {
    onFinish?.();
    void amplitudeLinkTrackEvent({
      buildType,
      language,
      linkName: capitalize(levelTwoMenu.name || ''),
      linkPath: levelTwoMenu.link || '',
      componentName: 'Header Second Level Menu',
      pageLevel,
      linkEventName: 'Header Second Level Link',
      linkTitle: pathToTitle(levelTwoMenu.link || ''),
    });
  };

  const selectedColor = isHarbour ? styling.colors.harbourGold : styling.colors.camelot;
  const notSelectedColor = isHarbour ? styling.colors.harbourGold : styling.colors.capeCod;

  return (
    <>
      <WebsiteLink
        to={levelTwoMenu.link}
        onFocus={() => {
          onSelectMenu();
        }}
        handleClick={handleClickSecondLevel}
      >
        <div
          key={levelTwoMenu.name}
          className={classNames(styles.item, styles.hover)}
          onPointerOver={() => {
            onSelectMenu();
          }}
        >
          <div className={classNames(styles.text, selected && styles.selected)}>
            {levelTwoMenu.name}
          </div>
          {hasLevelThreeItems &&
            (isMeridian ? (
              <RedesignRightChevronThin
                size="0.7rem"
                fill={selected ? styling.colors.meridianBlue : styling.colors.capeCod}
              />
            ) : (
              <ArrowRightThin size={16} fill={selected ? selectedColor : notSelectedColor} />
            ))}
        </div>
      </WebsiteLink>

      {hasLevelThreeItems && (
        <ul
          className={classNames(
            styles.levelThreeWrapper,
            selected ? styles.levelThreeVisible : styles.levelThreeHidden
          )}
          style={{ '--level-three-top': levelThreeTop } as React.CSSProperties}
          ref={updateRef}
        >
          {levelTwoMenu.levelThreeMenuItems.map((levelThreeMenu) => {
            const handleClickThirdLevel = () => {
              onFinish?.();
              void amplitudeLinkTrackEvent({
                buildType,
                language,
                linkName: capitalize(levelThreeMenu.name || ''),
                linkPath: levelThreeMenu.link || '',
                componentName: 'Header Third Level Menu',
                pageLevel,
                linkEventName: 'Header Third Level Link',
                linkTitle: pathToTitle(levelThreeMenu.link || ''),
              });
            };

            return (
              <li key={levelThreeMenu.name}>
                <WebsiteLink to={levelThreeMenu.link} handleClick={handleClickThirdLevel}>
                  <div key={levelThreeMenu.name} className={classNames(styles.item, styles.hover)}>
                    <div className={styles.text}>{levelThreeMenu.name}</div>
                  </div>
                </WebsiteLink>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
};

export default HeaderDesktopLevelTwoMenu;
